/**
 * Pure logic for the notifications triage inbox (design 17).
 *
 * Everything here is framework-free so it can be unit tested: tab filtering,
 * day grouping, stacking several updates on one entity into a single row, the
 * sentence each row reads as, and which comment triggered a notification.
 */
import type { NotificationDocument } from '@/lib/db/rxdb/schemas/notification'

export type NotificationTab = 'all' | 'mentions' | 'assigned'

export type DayBucket = 'today' | 'yesterday' | 'earlier'

export type NotificationIconKind =
  | 'mention'
  | 'comment'
  | 'assign'
  | 'reminder'
  | 'cycle'
  | 'status'
  | 'system'

/** Notification shape the inbox logic needs (a subset of the RxDB doc). */
export type InboxNotification = Pick<
  NotificationDocument,
  'id' | 'type' | 'entityType' | 'entityId' | 'isRead' | 'createdOn' | 'createdBy'
>

const MENTION_TYPES = new Set(['mention', 'description_mention', 'page_mention'])
const COMMENT_TYPES = new Set(['comment', 'page_comment'])
const DOC_ENTITY_TYPES = new Set(['document', 'page'])

const DAY_MS = 24 * 60 * 60 * 1000

export const isMentionType = (type: string) => MENTION_TYPES.has(type)
export const isCommentType = (type: string) => COMMENT_TYPES.has(type)
export const isDocEntityType = (entityType: string | null | undefined) =>
  entityType != null && DOC_ENTITY_TYPES.has(entityType)

/** Types that come from a comment, so the pane can highlight that comment. */
export const isCommentTriggered = (type: string) =>
  COMMENT_TYPES.has(type) || type === 'mention' || type === 'page_mention'

/** Reminder and system notifications show the message, never a conversation. */
export const hasConversation = (type: string) =>
  type !== 'reminder' && type !== 'system'

export function getIconKind(
  notification: Pick<InboxNotification, 'type' | 'entityType'>,
): NotificationIconKind {
  if (notification.entityType === 'cycle') return 'cycle'
  if (isMentionType(notification.type)) return 'mention'
  if (isCommentType(notification.type)) return 'comment'
  switch (notification.type) {
    case 'assignment':
      return 'assign'
    case 'reminder':
      return 'reminder'
    case 'status_change':
      return 'status'
    default:
      return 'system'
  }
}

export function matchesTab(notification: Pick<InboxNotification, 'type'>, tab: NotificationTab) {
  if (tab === 'mentions') return isMentionType(notification.type)
  if (tab === 'assigned') return notification.type === 'assignment'
  return true
}

export function filterByTab<T extends Pick<InboxNotification, 'type'>>(
  notifications: ReadonlyArray<T>,
  tab: NotificationTab,
): Array<T> {
  return notifications.filter((n) => matchesTab(n, tab))
}

export function countUnread(notifications: ReadonlyArray<Pick<InboxNotification, 'isRead'>>) {
  return notifications.reduce((sum, n) => (n.isRead ? sum : sum + 1), 0)
}

const startOfLocalDay = (ms: number) => {
  const d = new Date(ms)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

/** Today / Yesterday / Earlier, by local calendar day. Future times count as today. */
export function getDayBucket(timestamp: number, now: number): DayBucket {
  const today = startOfLocalDay(now)
  if (timestamp >= today) return 'today'
  // Step back from local midnight via a date at noon so DST shifts can't skip a day.
  const noonYesterday = new Date(today - DAY_MS / 2)
  noonYesterday.setHours(0, 0, 0, 0)
  if (timestamp >= noonYesterday.getTime()) return 'yesterday'
  return 'earlier'
}

type NonEmpty<T> = [T, ...Array<T>]

export type InboxEntry<T extends InboxNotification = InboxNotification> =
  | { kind: 'single'; id: string; notification: T }
  /** Several updates on one entity. `notifications` is newest first. */
  | { kind: 'stack'; id: string; notifications: NonEmpty<T> }

export interface InboxGroup<T extends InboxNotification = InboxNotification> {
  bucket: DayBucket
  entries: Array<InboxEntry<T>>
}

const BUCKET_ORDER: Array<DayBucket> = ['today', 'yesterday', 'earlier']

/**
 * Stacking rule: within one day group, two or more notifications about the same
 * entity (same entityType + entityId) collapse into one stacked row. Mentions
 * never stack (each one asks something of you), and notifications without an
 * entity never stack. The stack sits where its newest update would.
 */
export function stackKey(notification: InboxNotification): string | null {
  if (isMentionType(notification.type)) return null
  if (!notification.entityId) return null
  return `${notification.entityType ?? ''}:${notification.entityId}`
}

export function buildInboxGroups<T extends InboxNotification>(
  notifications: ReadonlyArray<T>,
  now: number,
): Array<InboxGroup<T>> {
  const sorted = [...notifications].sort((a, b) => b.createdOn - a.createdOn)
  const byBucket = new Map<DayBucket, Array<NonEmpty<T>>>()
  const slotByKey = new Map<string, NonEmpty<T>>()

  for (const notification of sorted) {
    const bucket = getDayBucket(notification.createdOn, now)
    const slots = byBucket.get(bucket) ?? []
    byBucket.set(bucket, slots)

    const key = stackKey(notification)
    const scopedKey = key ? `${bucket}|${key}` : null
    const existing = scopedKey ? slotByKey.get(scopedKey) : undefined
    if (existing) {
      existing.push(notification)
      continue
    }
    const slot: NonEmpty<T> = [notification]
    slots.push(slot)
    if (scopedKey) slotByKey.set(scopedKey, slot)
  }

  const groups: Array<InboxGroup<T>> = []
  for (const bucket of BUCKET_ORDER) {
    const slots = byBucket.get(bucket)
    if (!slots || slots.length === 0) continue
    groups.push({
      bucket,
      entries: slots.map((slot): InboxEntry<T> =>
        slot.length > 1
          ? { kind: 'stack', id: `stack:${slot[0].id}`, notifications: slot }
          : { kind: 'single', id: slot[0].id, notification: slot[0] },
      ),
    })
  }
  return groups
}

export function flattenEntries<T extends InboxNotification>(
  groups: ReadonlyArray<InboxGroup<T>>,
): Array<InboxEntry<T>> {
  return groups.flatMap((g) => g.entries)
}

/** The notification that represents an entry (the newest in a stack). */
export function entryPrimary<T extends InboxNotification>(entry: InboxEntry<T>): T {
  return entry.kind === 'single' ? entry.notification : entry.notifications[0]
}

export function entryNotifications<T extends InboxNotification>(entry: InboxEntry<T>): Array<T> {
  return entry.kind === 'single' ? [entry.notification] : entry.notifications
}

export function isEntryUnread(entry: InboxEntry): boolean {
  return entryNotifications(entry).some((n) => !n.isRead)
}

export function entryContains(entry: InboxEntry, notificationId: string): boolean {
  return entryNotifications(entry).some((n) => n.id === notificationId)
}

export type SentenceKey =
  | 'mention'
  | 'descriptionMention'
  | 'pageMention'
  | 'comment'
  | 'pageComment'
  | 'assignment'
  | 'statusChange'
  | 'reminder'
  | 'system'

export function getSentenceKey(type: string): SentenceKey {
  switch (type) {
    case 'mention':
      return 'mention'
    case 'description_mention':
      return 'descriptionMention'
    case 'page_mention':
      return 'pageMention'
    case 'comment':
      return 'comment'
    case 'page_comment':
      return 'pageComment'
    case 'assignment':
      return 'assignment'
    case 'status_change':
      return 'statusChange'
    case 'reminder':
      return 'reminder'
    default:
      return 'system'
  }
}

export type ActorRef =
  | { kind: 'you' }
  | { kind: 'member'; name: string }
  | { kind: 'unknown' }

export function resolveActor(
  createdBy: string | null | undefined,
  currentUserId: string | null | undefined,
  nameByUserId: ReadonlyMap<string, string>,
): ActorRef {
  if (createdBy && currentUserId && createdBy === currentUserId) return { kind: 'you' }
  const name = createdBy ? nameByUserId.get(createdBy) : undefined
  return name ? { kind: 'member', name } : { kind: 'unknown' }
}

/**
 * The sentence a row reads as, as an i18n key plus its values. The UI renders
 * it with <Trans> so the actor is bold. System notifications have no fixed
 * wording, so the row falls back to the notification's own title.
 */
export interface SentenceDescriptor {
  i18nKey: `sentence.${SentenceKey}` | 'sentence.stack'
  values: Record<string, string | number>
  /** True when the row should show `notification.title` instead of a sentence. */
  useTitle: boolean
}

export function buildSentence(
  notification: Pick<InboxNotification, 'type'>,
  actorLabel: string,
): SentenceDescriptor {
  const key = getSentenceKey(notification.type)
  return {
    i18nKey: `sentence.${key}`,
    values: { actor: actorLabel },
    useTitle: key === 'system',
  }
}

export function buildStackSentence(count: number, target: string): SentenceDescriptor {
  return { i18nKey: 'sentence.stack', values: { count, target }, useTitle: false }
}

/** Whether a row quotes the notification message (the comment text). */
export function showsQuote(notification: Pick<NotificationDocument, 'type' | 'message'>) {
  return (
    (isMentionType(notification.type) || isCommentType(notification.type)) &&
    !!notification.message?.trim()
  )
}

/**
 * Compact row time: "now", "12m", "3h" for today, weekday within the week,
 * then a short date. Returned as parts so the caller can localise.
 */
export type CompactTime =
  | { unit: 'now' }
  | { unit: 'minutes'; value: number }
  | { unit: 'hours'; value: number }
  | { unit: 'weekday' }
  | { unit: 'date' }

export function compactTime(timestamp: number, now: number): CompactTime {
  const diff = Math.max(0, now - timestamp)
  if (diff < 60_000) return { unit: 'now' }
  if (diff < 60 * 60_000) return { unit: 'minutes', value: Math.floor(diff / 60_000) }
  if (getDayBucket(timestamp, now) === 'today') {
    return { unit: 'hours', value: Math.floor(diff / (60 * 60_000)) }
  }
  if (timestamp >= startOfLocalDay(now) - 6 * DAY_MS) return { unit: 'weekday' }
  return { unit: 'date' }
}

/**
 * The comment that triggered a notification. Notifications carry no comment
 * id, so pick the comment by the notification's actor created closest to (and
 * not after, give or take a minute of clock skew) the notification.
 */
export const TRIGGER_TOLERANCE_MS = 60_000

export function findTriggeringComment<
  TComment extends { id: string; createdBy: string; createdOn: number },
>(
  comments: ReadonlyArray<TComment>,
  notification: Pick<InboxNotification, 'type' | 'createdBy' | 'createdOn'>,
  toleranceMs: number = TRIGGER_TOLERANCE_MS,
): TComment | null {
  if (!isCommentTriggered(notification.type)) return null
  let best: TComment | null = null
  for (const comment of comments) {
    if (comment.createdBy !== notification.createdBy) continue
    if (comment.createdOn > notification.createdOn + toleranceMs) continue
    if (!best || comment.createdOn > best.createdOn) best = comment
  }
  return best
}
